import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { GraphService } from 'src/app/services/graph/graph.service';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';
import { Error } from 'src/app/models/error.model';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';

@Component({
  selector: 'app-sharedmailboxes-table',
  templateUrl: './sharedmailboxes-table.component.html',
  styleUrl: './sharedmailboxes-table.component.scss',
  standalone: false,
})
export class SharedmailboxesTableComponent
  implements OnDestroy, OnInit
{
  dataSourceSharedMailBoxes: MatTableDataSource<string>;
  sharedMailBoxesNameFilter: FormControl = new FormControl('');
  defaultColumns: string[] = ['photo', 'displayName'];
  displayedColumnsSharedMailBoxes: string[] = [...this.defaultColumns];
  exchangeStop$: Subject<boolean> = new Subject<boolean>();
  isBusy: boolean;
  userSharedMailBoxes: string[] = [];
  hasClickedButton = false;
  filterValuesSharedMailBoxes = {
    displayName: '',
  };

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 40,
  };

  @Input() userEmail: string;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(private _toolBoxService: ToolBoxService,
    private _graphSerivce: GraphService,
    private _router: Router,
    private _localStorageService: LocalStorageService
  ) {}

  ngOnInit() {
    const sharedMailboxes: string[] = this._localStorageService.getItem(`${this.userEmail}-sharedMailboxes`);
    if (sharedMailboxes) {
      this.initializeDataSource(sharedMailboxes);
    }
  }

  async onButtonClick() {
    this.setVisibleColumns();
    this.initSharemailboxesFilter();
    const sharedMailboxes = await this.getUserSharedMailboxes();
    this.initializeDataSource(sharedMailboxes);
  }

  ngOnDestroy(): void {
    this.exchangeStop$.next(true);
    this.exchangeStop$.complete();
    this._localStorageService.removeItem(`${this.userEmail}-sharedMailboxes`);
  }

  async getUserSharedMailboxes(): Promise<string[]> {
    this.isBusy = true;
    try {
      const sharedMailboxes = await lastValueFrom(
        this._graphSerivce.getUserSharedMailboxes(this.userEmail).pipe(takeUntil(this.exchangeStop$))
      );
      this._localStorageService.setItem(`${this.userEmail}-sharedMailboxes`, sharedMailboxes);
      return sharedMailboxes;
    } catch (e) {
      let customError: Error = { error: undefined };
      if (e.error) {
        customError = JSON.parse(e.error);
      }
      if (customError.error !== undefined) {
        if (customError.error.code == 'ERR404') {
          this._router.navigate(['/errors/error-404']);
        }
      }
      return [];
    } finally {
      this.hasClickedButton = true;
      this.isBusy = false;
    }
  }

  async initializeDataSource(sharedmailboxes?: string[]) {
    this.userSharedMailBoxes = sharedmailboxes
    this.dataSourceSharedMailBoxes = new MatTableDataSource(
      this.userSharedMailBoxes
    );

    setTimeout(() => {
      this.dataSourceSharedMailBoxes.sort = this.sort;
      this.dataSourceSharedMailBoxes.paginator = this.paginator;
    })
    
    this.dataSourceSharedMailBoxes.filterPredicate =
      this.createSharemailboxesFilter();
  }

  createSharemailboxesFilter(): (data: string, filter: string) => boolean {
    const filterFunction = (data, filter): boolean => {
      const searchTerms = JSON.parse(filter);
      return data.toLowerCase().indexOf(searchTerms.displayName) !== -1;
    };
    return filterFunction;
  }

  initSharemailboxesFilter() {
    this.sharedMailBoxesNameFilter.valueChanges.subscribe(name => {
      this.filterValuesSharedMailBoxes.displayName = name.toLowerCase();
      this.dataSourceSharedMailBoxes.filter = JSON.stringify(
        this.filterValuesSharedMailBoxes
      );
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setVisibleColumns();
  }

  setVisibleColumns() {
    this.displayedColumnsSharedMailBoxes = [...this.defaultColumns];
    if (window.innerWidth < 420) {
      this._toolBoxService.hideTableColumnWithoutExpand(
        'photo',
        this.displayedColumnsSharedMailBoxes
      );
    }
  }
}
